import { createSlice } from '@reduxjs/toolkit';
import {
  Achievement,
  AchievementsFullPayload,
  EarnConfigPayload,
  fetchAchievements,
  fetchAllAchievements,
  fetchEarnConfig,
  updateFeaturedAchievements,
} from '../thunks/achievementThunks';

interface AchievementState {
  byUsername: Record<string, Achievement[]>;
  fullByUsername: Record<string, AchievementsFullPayload>;
  earnConfig: EarnConfigPayload | null;
  earnConfigLoading: boolean;
  loadingFor: string | null;
  loadingFullFor: string | null;
  savingFeatured: boolean;
  error: string | null;
}

const initialState: AchievementState = {
  byUsername: {},
  fullByUsername: {},
  earnConfig: null,
  earnConfigLoading: false,
  loadingFor: null,
  loadingFullFor: null,
  savingFeatured: false,
  error: null,
};

const achievementSlice = createSlice({
  name: 'achievement',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAchievements.pending, (state, action) => {
        state.loadingFor = action.meta.arg;
        state.error = null;
      })
      .addCase(fetchAchievements.fulfilled, (state, action) => {
        state.byUsername[action.payload.username] = action.payload.achievements;
        state.loadingFor = null;
      })
      .addCase(fetchAchievements.rejected, (state, action) => {
        state.loadingFor = null;
        state.error = action.payload as string;
      })
      .addCase(fetchAllAchievements.pending, (state, action) => {
        state.loadingFullFor = action.meta.arg;
        state.error = null;
      })
      .addCase(fetchAllAchievements.fulfilled, (state, action) => {
        state.fullByUsername[action.payload.username] = action.payload.payload;
        state.loadingFullFor = null;
      })
      .addCase(fetchAllAchievements.rejected, (state, action) => {
        state.loadingFullFor = null;
        state.error = action.payload as string;
      })
      .addCase(fetchEarnConfig.pending, (state) => {
        state.earnConfigLoading = true;
      })
      .addCase(fetchEarnConfig.fulfilled, (state, action) => {
        state.earnConfig = action.payload;
        state.earnConfigLoading = false;
      })
      .addCase(fetchEarnConfig.rejected, (state) => {
        state.earnConfigLoading = false;
      })
      .addCase(updateFeaturedAchievements.pending, (state) => {
        state.savingFeatured = true;
        state.error = null;
      })
      .addCase(updateFeaturedAchievements.fulfilled, (state, action) => {
        state.savingFeatured = false;
        const { username, featured } = action.payload;
        // Patch both caches so any selector reading byUsername (profile
        // sidebar via AchievementTokens) or fullByUsername (achievements
        // page) sees the new featuredOrder without a refetch.
        // featuredOrder = position in the array, or null when the type
        // isn't pinned anymore.
        const orderOf = (type: Achievement['type']): number | null => {
          const idx = featured.indexOf(type);
          return idx === -1 ? null : idx;
        };
        const list = state.byUsername[username];
        if (list) {
          list.forEach((a) => {
            a.featuredOrder = orderOf(a.type);
          });
        }
        const full = state.fullByUsername[username];
        if (full) {
          full.earned.forEach((a) => {
            a.featuredOrder = orderOf(a.type);
          });
        }
      })
      .addCase(updateFeaturedAchievements.rejected, (state, action) => {
        state.savingFeatured = false;
        state.error = action.payload as string;
      });
  },
});

export default achievementSlice.reducer;
